import React from 'react';
import { Link } from 'react-router-dom';
import dashboardService from '../../services/dashboardService';
import { ROUTES } from '../../constants/routes';

const DashboardHeroCards = ({ metrics = {}, activePreset = '30d' }) => {
  const isAllTime = activePreset === 'all';
  const displayVolume = isAllTime ? metrics.grossVolume : (metrics.periodGrossVolume || metrics.grossVolume || 0);
  const displayCashback = isAllTime ? metrics.totalCashback : (metrics.periodCashback || metrics.totalCashback || 0);
  const pendingSnaps = metrics.pendingSnaps || 0;
  const pendingReversals = metrics.pendingReversals || 0;
  const totalQueuePressure = pendingSnaps + pendingReversals;

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6'>
      {/* 1. Gross Transaction Volume Card */}
      <div className='relative overflow-hidden bg-white p-5 rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow group'>
        <div className='flex items-center justify-between'>
          <span className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            Gross GMV Volume
          </span>
          <div className='w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors'>
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' />
            </svg>
          </div>
        </div>

        <div className='mt-3'>
          <h3 className='text-2xl font-bold text-gray-900 tracking-tight'>
            {dashboardService.formatCurrency(displayVolume)}
          </h3>
          <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
            <span>Cashback: <strong className='text-emerald-600'>{dashboardService.formatCurrency(displayCashback)}</strong></span>
            <span>Avg: <strong>{dashboardService.formatCurrency(metrics.averageOrderValue || 0)}</strong></span>
          </div>
        </div>
      </div>

      {/* 2. Merchant Network Card */}
      <div className='relative overflow-hidden bg-white p-5 rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow group'>
        <div className='flex items-center justify-between'>
          <span className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            Merchant Network
          </span>
          <div className='w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors'>
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' />
            </svg>
          </div>
        </div>

        <div className='mt-3'>
          <div className='flex items-baseline gap-2'>
            <h3 className='text-2xl font-bold text-gray-900 tracking-tight'>
              {dashboardService.formatNumber(metrics.activeMerchants || 0)}
            </h3>
            <span className='text-xs text-gray-400 font-medium'>
              / {dashboardService.formatNumber(metrics.totalMerchants || 0)} total
            </span>
          </div>
          <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
            <span>Balance Pool:</span>
            <strong className='text-gray-900'>{dashboardService.formatCurrency(metrics.totalMarketingBalance || 0)}</strong>
          </div>
        </div>
      </div>

      {/* 3. Community Network Card */}
      <div className='relative overflow-hidden bg-white p-5 rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow group'>
        <div className='flex items-center justify-between'>
          <span className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            Community Network
          </span>
          <div className='w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors'>
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' />
            </svg>
          </div>
        </div>

        <div className='mt-3'>
          <div className='flex items-baseline gap-2'>
            <h3 className='text-2xl font-bold text-gray-900 tracking-tight'>
              {dashboardService.formatNumber(metrics.totalCommunity || metrics.totalShoppers || 0)}
            </h3>
            {metrics.newCommunityInPeriod > 0 && (
              <span className='inline-flex items-center text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded'>
                +{dashboardService.formatNumber(metrics.newCommunityInPeriod)}
              </span>
            )}
          </div>
          <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
            <span>Shoppers: <strong className='text-gray-700'>{dashboardService.formatNumber(metrics.totalShoppers || 0)}</strong></span>
            <span>Creators: <strong className='text-gray-700'>{dashboardService.formatNumber(metrics.totalCreators || 0)}</strong></span>
          </div>
        </div>
      </div>

      {/* 4. Action Queue Pressure Card */}
      <div className={`relative overflow-hidden bg-white p-5 rounded-xl border transition-all group ${
        totalQueuePressure > 0 ? 'border-amber-300 shadow-sm ring-1 ring-amber-200' : 'border-gray-200 shadow-xs'
      }`}>
        <div className='flex items-center justify-between'>
          <span className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            Action Queue
          </span>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
            totalQueuePressure > 0 ? 'bg-amber-100 text-amber-700 animate-pulse' : 'bg-gray-100 text-gray-500'
          }`}>
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z' />
            </svg>
          </div>
        </div>

        <div className='mt-3'>
          <div className='flex items-baseline gap-2'>
            <h3 className={`text-2xl font-bold tracking-tight ${totalQueuePressure > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
              {pendingSnaps}
            </h3>
            <span className='text-xs font-semibold text-gray-500'>
              Pending 1-Snaps
            </span>
          </div>
          <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
            <span>Reversals: <strong className='text-gray-800'>{pendingReversals}</strong></span>
            <Link
              to={ROUTES.ONE_SNAP}
              className='text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-0.5'
            >
              Review queue →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeroCards;
